import { formatNumberKMB, formatIndianCurrency } from "./helpers";
import colors from "../config/colors";

/**
 * Campaigns with an ID below this threshold are legacy — edit, upload, and
 * update actions are disabled in the UI. Set to a large sentinel value for now;
 * replace with the real cutoff once determined.
 */
export const LEGACY_CAMPAIGN_ID_THRESHOLD = 7565;

/** Max content URLs per CSV upload in campaign tracking creation/edit. */
export const CAMPAIGN_CONTENT_UPLOAD_MAX_ITEMS = 10000;

/** Batch size for sequential validate-campaign-content calls (keep under gateway timeout). */
export const CAMPAIGN_CONTENT_VALIDATE_BATCH_SIZE = 500;

/** `/me` `data.features.campaigns[]` — when slug present → tabbed Tracking landing (All / Execution / Tracking). */
export const ME_CAMPAIGN_FEATURE_EXECUTION_TRACKING = "execution_tracking";

/** BrandDashboardFAQ id for "How is CPV (Cost Per View) calculated?" — used for Help deep links. */
export const ER_FAQ_ID = 13;
export const ER_FAQ_HELP_PATH = `/faqs/home?faqId=${ER_FAQ_ID}`;
/** Open Help → FAQs with this question expanded and highlighted. */
export const CPV_FAQ_ID = 14;
export const CPV_FAQ_HELP_PATH = `/faqs/home?faqId=${CPV_FAQ_ID}`;

export const TRACKING_TABLE_HEADERS = [
  {
    id: "campaign_name",
    label: "Campaign name",
    align: "left",
    minWidth: 180,
    sortable: true,
  },
  {
    id: "posts",
    label: "Posts",
    align: "center",
    minWidth: 70,
    sortable: true,
  },
  {
    id: "views",
    label: "Views",
    align: "center",
    minWidth: 100,
    sortable: true,
  },
  {
    id: "engagement_rate",
    label: "E.R.%",
    align: "center",
    minWidth: 80,
    sortable: true,
    tooltip: "How is ER calculated?",
    tooltipLink: ER_FAQ_HELP_PATH,
  },

  {
    id: "auto_dms",
    label: "Auto DMs",
    align: "center",
    minWidth: 90,
    sortable: true,
  },
  {
    id: "clicks",
    label: "Clicks",
    align: "center",
    minWidth: 80,
    sortable: true,
  },
  {
    id: "ctr",
    label: "CTR",
    align: "center",
    minWidth: 70,
    sortable: true,
  },
  {
    id: "orders",
    label: "Orders",
    align: "center",
    minWidth: 80,
    sortable: true,
  },
  {
    id: "conversion",
    label: "Conversion",
    align: "center",
    minWidth: 100,
    sortable: true,
  },
  {
    id: "sales",
    label: "Sales",
    align: "center",
    minWidth: 100,
    sortable: true,
  },
  {
    id: "cpv",
    label: "CPV",
    align: "center",
    minWidth: 70,
    sortable: true,
    tooltip: "How is CPV calculated?",
    tooltipLink: CPV_FAQ_HELP_PATH,
  },
  {
    id: "actions",
    label: "",
    align: "center",
    minWidth: 50,
    sortable: false,
  },
];

export const EXECUTION_TABLE_HEADERS = [
  {
    id: "campaign_name",
    label: "Campaign name",
    align: "left",
    minWidth: 180,
    sortable: true,
  },
  {
    id: "date_created",
    label: "Date Created",
    align: "center",
    minWidth: 120,
    sortable: true,
  },
  {
    id: "total_creators",
    label: "Participant creators",
    align: "center",
    minWidth: 110,
    sortable: true,
  },
  {
    id: "product",
    label: "Product Delivered",
    align: "center",
    minWidth: 90,
    sortable: true,
  },
  {
    id: "script",
    label: "Script Approved",
    align: "center",
    minWidth: 80,
    sortable: true,
  },
  {
    id: "draft",
    label: "Draft Approved",
    align: "center",
    minWidth: 80,
    sortable: true,
  },
  {
    id: "content",
    label: "Content Live",
    align: "center",
    minWidth: 90,
    sortable: true,
  },
  // TODO: Actions column to add later
  // {
  //   id: "actions",
  //   label: "",
  //   align: "center",
  //   minWidth: 50,
  //   sortable: false,
  // },
];

export const CAMPAIGN_TABS = {
  REQUESTED: "requested",
  EXECUTION: "execution",
  TRACKING: "tracking",
};

// Requested tab: the brand's campaign requests. Name and status are not sortable.
export const REQUESTED_TABLE_HEADERS = [
  { id: "campaign_name", label: "Campaign name", align: "left", minWidth: 180 },
  {
    id: "budget",
    label: "Budget",
    align: "center",
    minWidth: 110,
    sortable: true,
  },
  {
    id: "creator_count",
    label: "Number of creators",
    align: "center",
    minWidth: 120,
    sortable: true,
  },
  {
    id: "content_live_date",
    label: "Content live date",
    align: "center",
    minWidth: 130,
    sortable: true,
  },
  {
    id: "date_created",
    label: "Date submitted",
    align: "center",
    minWidth: 130,
    sortable: true,
  },
  { id: "status", label: "Status", align: "center", minWidth: 110 },
];

export const TRACKING_3DOT_ACTIONS = {
  UPLOAD_SKU: "upload_sku",
  EDIT: "edit",
  DELETE: "delete",
};

export const TRACKING_PLATFORMS = [
  {
    id: "amazon",
    label: "Amazon",
    brandIds: [7],
    color: "#FF9900",
    tooltip: "Sale data updates once every day.",
  },
  {
    id: "flipkart",
    label: "Flipkart",
    brandIds: [303],
    color: "#2874F0",
    tooltip: "Sale data updates every 5–6 hours.",
  },
  {
    id: "myntra",
    label: "Myntra",
    brandIds: [227],
    color: "#FF3F6C",
    tooltip: "Sale data updates in 4–5 hours.",
  },
  {
    id: "nykaa",
    label: "Nykaa",
    brandIds: [302, 308],
    color: "#FC2779",
    tooltip:
      "Sale data is updated in 24 hours. Product-level sale data is not available for Nykaa.",
  },
  {
    id: "tira",
    label: "Tira",
    brandIds: [273],
    color: "#000000",
    tooltip: "Sale data updates every 5–6 hours.",
  },
  {
    id: "blinkit",
    label: "Blinkit",
    brandIds: [7572],
    color: "#000000",
    tooltip:
      "Click data updates every 5–6 hours. Product-level sale data is not available for Blinkit.",
  },
  {
    id: "zepto",
    label: "Zepto",
    brandIds: [358],
    color: "#000000",
    tooltip:
      "Click data updates every 5–6 hours. Product-level sale data is not available for Zepto.",
  },
  {
    id: "instamart",
    label: "Instamart",
    brandIds: [8760],
    color: "#000000",
    tooltip:
      "Click data updates every 5–6 hours. Product-level sale data is not available for Instamart.",
  },
  {
    id: "purplle",
    label: "Purplle",
    brandIds: [277],
    color: "#000000",
    tooltip:
      "Click data updates every 5–6 hours. Product-level sale data is not available for Purplle.",
  },
  {
    id: "website",
    label: "Website",
    brandIds: null,
    color: "#4CAF50",
    tooltip: "Sale data from your brand's website updates in 4–5 hours.",
  },
];

export const PLATFORM_BRAND_MAP = Object.fromEntries(
  TRACKING_PLATFORMS.filter((p) => p.brandIds).map((p) => [p.id, p.brandIds]),
);

export const ALL_PLATFORM_BRAND_IDS = TRACKING_PLATFORMS.flatMap(
  (p) => p.brandIds || [],
);

export const MARKETPLACE_BRAND_NAME_PLATFORMS = TRACKING_PLATFORMS.filter((p) =>
  ["amazon", "flipkart", "myntra", "nykaa", "tira", "blinkit"].includes(p.id),
).map((p) => ({ id: p.brandIds[0], label: p.label }));

export const formatCellValue = (id, value) => {
  if (value === null || value === undefined) return "-";
  switch (id) {
    case "views":
    case "clicks":
    case "orders":
    case "auto_dms":
    case "posts":
      return typeof value === "number" ? formatNumberKMB(value) : value;
    case "sales":
      return typeof value === "number" ? formatIndianCurrency(value) : value;
    case "cpv":
      return typeof value === "number" ? `₹${value.toFixed(2)}` : value;
    case "engagement_rate":
    case "ctr":
    case "conversion":
      return typeof value === "number" || typeof value === "string"
        ? `${value}%`
        : value;
    default:
      return String(value);
  }
};

const EXECUTION_NA_STAGE_COLUMNS = new Set(["product", "script", "draft"]);

export const formatExecutionCellValue = (id, value) => {
  if (value === null || value === undefined) {
    return EXECUTION_NA_STAGE_COLUMNS.has(id) ? "NA" : "-";
  }
  switch (id) {
    case "total_creators":
    case "product":
    case "script":
    case "draft":
    case "content":
      return typeof value === "number" ? formatNumberKMB(value) : value;
    default:
      return String(value);
  }
};

export const EXECUTION_STAGE_TABS = {
  ALL: "all",
  PRODUCT: "product",
  SCRIPT: "script",
  DRAFT: "draft",
  CONTENT: "content",
  COMPLETE: "complete",
};

export const EXECUTION_CREATOR_STATUS = {
  IN_PROGRESS: "Pending",
  SUBMITTED: "Submitted",
  REWORK_IN_PROGRESS: "Reworking",
  REWORK_SUBMITTED: "Resubmitted",
  APPROVED: "Approved",
  ORDERED: "Ordered",
  DELIVERED: "Delivered",
  COMPLETE: "Complete",
  COMPILING: "Compiling",
};

/** Max width for @username in execution summary creator column (ellipsis + tooltip). */
export const EXECUTION_CREATOR_USERNAME_MAX_WIDTH = 130;
export const EXECUTION_CREATOR_USERNAME_MAX_WIDTH_COMPACT = 140;

export const EXECUTION_CREATOR_TABLE_HEADERS = [
  {
    id: "creator",
    label: "",
    align: "left",
    sortable: false,
    width: "16%",
  },
  {
    id: "follower_count",
    label: "Category, Followers",
    align: "center",
    sortable: true,
    width: "9%",
  },
  {
    id: "product",
    label: "Product",
    align: "center",
    sortable: false,
    width: "9%",
  },
  {
    id: "script",
    label: "Script",
    align: "center",
    sortable: false,
    width: "9%",
  },
  {
    id: "draft",
    label: "Draft",
    align: "center",
    sortable: false,
    width: "9%",
  },
  {
    id: "content",
    label: "Content",
    align: "center",
    sortable: false,
    width: "9%",
  },
  {
    id: "remarks",
    label: "Remarks",
    align: "center",
    sortable: false,
    width: "10%",
  },
  {
    id: "timeline",
    label: "Last Action",
    align: "center",
    sortable: true,
    width: "11%",
  },
  {
    id: "action",
    label: "Action",
    align: "center",
    sortable: false,
    width: "10%",
  },
];

/** Drawer layout: Remarks replaces Status; stage + pillar status live under username. */
export const EXECUTION_COMPACT_HEADERS = [
  { id: "creator", label: "", align: "left", width: "45%", sortable: false },
  {
    id: "remarks",
    label: "Remarks",
    align: "center",
    width: "30%",
    sortable: false,
  },
  {
    id: "timeline",
    label: "Last Action",
    align: "center",
    width: "25%",
    sortable: true,
  },
];

export const EXECUTION_STATUS_COLORS = {
  Pending: {
    backgroundColor: "#FFF7AE",
    textColor: "#998D1F",
    iconColor: "#998D1F",
    iconType: "clock",
  },
  Submitted: {
    backgroundColor: "#C2D0F2",
    textColor: "#144CC7",
    iconColor: "#144CC7",
    iconType: "dot",
  },
  Reworking: {
    backgroundColor: "#FFF7AE",
    textColor: "#998D1F",
    iconColor: "#998D1F",
    iconType: "clock",
  },
  Resubmitted: {
    backgroundColor: "#C2D0F2",
    textColor: "#144CC7",
    iconColor: "#144CC7",
    iconType: "dot",
  },
  Rework: {
    backgroundColor: "#FFF7AE",
    textColor: "#998D1F",
    iconColor: "#998D1F",
    iconType: "clock",
  },
  Approved: {
    backgroundColor: "#D1FAE5",
    textColor: "#065F46",
    iconColor: "#065F46",
    iconType: "check",
  },
  Ordered: {
    backgroundColor: "#C2D0F2",
    textColor: "#144CC7",
    iconColor: "#144CC7",
    iconType: "check",
  },
  Delivered: {
    backgroundColor: "#D1FAE5",
    textColor: "#065F46",
    iconColor: "#065F46",
    iconType: "check",
  },
  Cancelled: {
    backgroundColor: "#FEE2E2",
    textColor: "#B91C1C",
    iconColor: "#B91C1C",
    iconType: "dot",
  },
  Complete: {
    backgroundColor: "#D1FAE5",
    textColor: "#065F46",
    iconColor: "#065F46",
    iconType: "check",
  },
  Compiling: {
    backgroundColor: "#FFF7AE",
    textColor: "#998D1F",
    iconColor: "#998D1F",
    iconType: "clock",
  },
  /** Pipeline pillar — `current_stage` from API (e.g. `product`). */
  Product: {
    backgroundColor: "#CCFBF1",
    textColor: "#0F766E",
    iconColor: "#0F766E",
    iconType: "dot",
  },
  Script: {
    backgroundColor: "#E8D4F5",
    textColor: "#6B2F8C",
    iconColor: "#6B2F8C",
    iconType: "dot",
  },
  Draft: {
    backgroundColor: "#D4E8F5",
    textColor: "#2F5F8C",
    iconColor: "#2F5F8C",
    iconType: "dot",
  },
  Content: {
    backgroundColor: "#F5DFD4",
    textColor: "#8C4F2F",
    iconColor: "#8C4F2F",
    iconType: "dot",
  },
};

/** API sends pipeline slugs lowercase (`script`); palette keys are Title Case (`Script`). */
const EXECUTION_STAGE_SLUG_TO_COLOR_KEY = {
  product: "Product",
  script: "Script",
  draft: "Draft",
  content: "Content",
  complete: "Complete",
};

const _executionStatusFallbackBadge = {
  backgroundColor: colors.gray100,
  textColor: colors.gray200,
  iconColor: colors.gray200,
  iconType: "dot",
};

export const getStatusBadgeProps = (status) => {
  if (status == null || status === "") {
    return _executionStatusFallbackBadge;
  }
  const direct = EXECUTION_STATUS_COLORS[status];
  if (direct) return direct;
  if (typeof status === "string") {
    const mappedKey = EXECUTION_STAGE_SLUG_TO_COLOR_KEY[status.toLowerCase()];
    if (mappedKey) {
      const mapped = EXECUTION_STATUS_COLORS[mappedKey];
      if (mapped) return mapped;
    }
  }
  return _executionStatusFallbackBadge;
};

export const formatTimelineDate = (dateStr) => {
  if (!dateStr) return "-";

  const now = new Date();
  // Truncate to milliseconds (JS can't parse 6-digit fractional seconds)
  const normalized = String(dateStr).replace(/(\.\d{3})\d+/, "$1");
  const target = new Date(normalized);

  const diffMs = target - now;
  const diffMins = Math.round(diffMs / (1000 * 60));
  const diffHours = Math.floor(Math.abs(diffMs) / (1000 * 60 * 60));

  if (Math.abs(diffMins) < 60) {
    if (Math.abs(diffMins) <= 1) return diffMins >= 0 ? "Today" : "Just now";
    if (diffMins > 0) return `in ${diffMins} mins`;
    return `${Math.abs(diffMins)} mins ago`;
  }

  if (Math.abs(diffMs) < 24 * 60 * 60 * 1000) {
    if (diffMs > 0) return diffHours === 1 ? "in 1 hr" : `in ${diffHours} hrs`;
    return diffHours === 1 ? "1 hr ago" : `${diffHours} hrs ago`;
  }

  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const targetDay = new Date(dateStr);
  targetDay.setHours(0, 0, 0, 0);
  const diff = Math.floor((targetDay - today) / (1000 * 60 * 60 * 24));

  if (diff === 0) return "Today";
  if (diff === 1) return "Tomorrow";
  if (diff === -1) return "1 day ago";
  if (diff < -1) return `${Math.abs(diff)} days ago`;
  return `${diff} days`;
};

export const CAMPAIGN_PAGE_RIGHT_PADDING = 60;

export const EXECUTION_HEADER_STICKY_TOP = -16;

export const EXECUTION_DRAWER_VERTICAL_TAB_WIDTH = 36;
export const EXECUTION_DRAWER_FIRST_LEVEL_RATIO = 273 / 1440;
export const EXECUTION_DRAWER_SUBMISSIONS_RATIO = 468 / 1440;

/** Script / Draft / Content — API enum values for execution summary `stageStatus`. */
const EXECUTION_STAGE_STATUS_CHILDREN_SCRIPT_LIKE = [
  { value: "IN_PROGRESS", label: "Pending" },
  { value: "SUBMITTED", label: "Submitted" },
  { value: "REWORK_IN_PROGRESS", label: "Reworking" },
  { value: "REWORK_SUBMITTED", label: "Resubmitted" },
  { value: "COMPLETE", label: "Complete" },
];

const EXECUTION_STAGE_STATUS_CHILDREN_DRAFT = [
  { value: "IN_PROGRESS", label: "Pending" },
  { value: "SUBMITTED", label: "Submitted" },
  { value: "COMPILING", label: "Compiling" },
  { value: "REWORK_IN_PROGRESS", label: "Reworking" },
  { value: "REWORK_SUBMITTED", label: "Resubmitted" },
  { value: "COMPLETE", label: "Complete" },
];

/**
 * Two-level Status dropdown: stage → statuses (API values in `children[].value`).
 */
export const EXECUTION_STAGE_STATUS_OPTIONS = [
  {
    value: "product",
    label: "Product",
    children: [
      { value: "IN_PROGRESS", label: "Pending" },
      { value: "ORDERED", label: "Ordered" },
      { value: "DELIVERED", label: "Delivered" },
      { value: "CANCELLED", label: "Cancelled" },
    ],
  },
  {
    value: "script",
    label: "Script",
    children: EXECUTION_STAGE_STATUS_CHILDREN_SCRIPT_LIKE,
  },
  {
    value: "draft",
    label: "Draft",
    children: EXECUTION_STAGE_STATUS_CHILDREN_DRAFT,
  },
  {
    value: "content",
    label: "Content",
    children: EXECUTION_STAGE_STATUS_CHILDREN_SCRIPT_LIKE,
  },
];

export const EXECUTION_CREATOR_TYPE_OPTIONS = [
  { value: "Mega", label: "Mega (500k+)" },
  { value: "Macro", label: "Macro (100k-500k)" },
  { value: "Mid", label: "Mid (20k-100k)" },
  { value: "Micro", label: "Micro (5k-20k)" },
  { value: "Nano", label: "Nano (1k-5k)" },
];

/** `/tracking/detail?id=sample` — static analytics id (no backend campaign) */
export const SAMPLE_CAMPAIGN_ID = "sample";

/**
 * Brand IDs aligned with `TRACKING_PLATFORMS` / drawer chips (Amazon → Website).
 * `performanceByBrandId` splits must sum to row clicks, orders, and sales for aggregate totals.
 */
const PB = {
  AMAZON: 7,
  FLIPKART: 303,
  MYNTRA: 227,
  NYKAA: 302,
  TIRA: 273,
  WEBSITE: 999991,
};

/** Funnel table rows (shape matches `mapPostFromAPI` output) */
export const SAMPLE_FUNNEL_POSTS = [
  {
    id: "sample-post-1",
    cvxpId: "sample-cvxp-1",
    postId: "sample-post-1",
    postUrl: "https://www.instagram.com/reel/sample-lipsticks/",
    post_thumbnail:
      "https://images.unsplash.com/photo-1522335789203-aabd1fc54bc9?w=400&h=533&fit=crop",
    creatorUsername: "makeupbyzainabfaiz",
    creatorId: null,
    status: null,
    views: 31800,
    engagementRate: 9.2,
    autoDms: 648,
    clicks: 130,
    ctr: 2.3,
    orders: 12,
    conversion: 5.1,
    sales: 950000,
    cpv: 95,
    likes: 6148,
    comments: 783,
    shares: 7749,
    saves: 1847,
    addedOn: null,
    performanceByBrandId: {
      [PB.AMAZON]: { clicks: 45, orders: 5, sales: 380000 },
      [PB.FLIPKART]: { clicks: 32, orders: 3, sales: 240000 },
      [PB.MYNTRA]: { clicks: 22, orders: 2, sales: 150000 },
      [PB.NYKAA]: { clicks: 18, orders: 1, sales: 120000 },
      [PB.TIRA]: { clicks: 9, orders: 1, sales: 45000 },
      [PB.WEBSITE]: { clicks: 4, orders: 0, sales: 15000 },
    },
  },
  {
    id: "sample-post-2",
    cvxpId: "sample-cvxp-2",
    postId: "sample-post-2",
    postUrl: "https://www.instagram.com/p/sample-skincare/",
    post_thumbnail:
      "https://images.unsplash.com/photo-1556228720-195a672e8a03?w=400&h=533&fit=crop",
    creatorUsername: "glow_skin_riya",
    creatorId: null,
    status: "Amazon pending",
    views: 89200,
    engagementRate: 6.8,
    autoDms: 210,
    clicks: 88,
    ctr: 1.9,
    orders: 7,
    conversion: 4.2,
    sales: 420000,
    cpv: 88,
    likes: 4200,
    comments: 310,
    shares: 1200,
    saves: 890,
    addedOn: null,
    isWhitelistingRequested: false,
    performanceByBrandId: {
      [PB.AMAZON]: { clicks: 0, orders: 0, sales: 0 },
      [PB.FLIPKART]: { clicks: 28, orders: 3, sales: 180000 },
      [PB.MYNTRA]: { clicks: 22, orders: 2, sales: 120000 },
      [PB.NYKAA]: { clicks: 18, orders: 2, sales: 95000 },
      [PB.TIRA]: { clicks: 12, orders: 0, sales: 25000 },
      [PB.WEBSITE]: { clicks: 8, orders: 0, sales: 0 },
    },
  },
  {
    id: "sample-post-3",
    cvxpId: "sample-cvxp-3",
    postId: "sample-post-3",
    postUrl: "https://www.instagram.com/p/sample-outfit/",
    post_thumbnail:
      "https://images.unsplash.com/photo-1515886657613-9f3515b0c78f?w=400&h=533&fit=crop",
    creatorUsername: "stylebyananya",
    creatorId: null,
    status: "Non-Wishlink",
    views: 156000,
    engagementRate: 5.4,
    autoDms: 412,
    clicks: 205,
    ctr: 2.1,
    orders: 18,
    conversion: 4.8,
    sales: 1280000,
    cpv: 72,
    likes: 8900,
    comments: 560,
    shares: 2100,
    saves: 3200,
    addedOn: null,
    productMessage:
      "Product-level attribution is limited until this creator links the post on Wishlink.",
    performanceByBrandId: {
      [PB.AMAZON]: { clicks: 62, orders: 6, sales: 420000 },
      [PB.FLIPKART]: { clicks: 48, orders: 5, sales: 350000 },
      [PB.MYNTRA]: { clicks: 38, orders: 4, sales: 280000 },
      [PB.NYKAA]: { clicks: 32, orders: 2, sales: 180000 },
      [PB.TIRA]: { clicks: 18, orders: 1, sales: 70000 },
      [PB.WEBSITE]: { clicks: 7, orders: 0, sales: 0 },
    },
  },
  {
    id: "sample-post-4",
    cvxpId: "sample-cvxp-4",
    postId: "sample-post-4",
    postUrl: "https://www.youtube.com/watch?v=dQw4w9WgXcQ",
    post_thumbnail:
      "https://images.unsplash.com/photo-1611162617474-5b21e879e113?w=400&h=533&fit=crop",
    creatorUsername: "shorts_deals_hunter",
    creatorId: null,
    status: "Non-Instagram",
    views: 42000,
    engagementRate: 4.1,
    autoDms: 0,
    clicks: 12,
    ctr: 0.9,
    orders: 1,
    conversion: 2.7,
    sales: 8500,
    cpv: 210,
    likes: 1200,
    comments: 88,
    shares: 340,
    saves: 210,
    addedOn: null,
    productMessage:
      "This URL is not from Instagram. Add an Instagram post URL for full funnel tracking.",
    performanceByBrandId: {
      [PB.AMAZON]: { clicks: 4, orders: 0, sales: 0 },
      [PB.FLIPKART]: { clicks: 3, orders: 1, sales: 8500 },
      [PB.MYNTRA]: { clicks: 2, orders: 0, sales: 0 },
      [PB.NYKAA]: { clicks: 2, orders: 0, sales: 0 },
      [PB.TIRA]: { clicks: 1, orders: 0, sales: 0 },
      [PB.WEBSITE]: { clicks: 0, orders: 0, sales: 0 },
    },
  },
  {
    id: "sample-post-5",
    cvxpId: "sample-cvxp-5",
    postId: "sample-post-5",
    postUrl: "https://www.instagram.com/reel/sample-wishlink-pending/",
    post_thumbnail:
      "https://images.unsplash.com/photo-1596462502278-27bfdc403362?w=400&h=533&fit=crop",
    creatorUsername: "wishlink_creators",
    creatorId: null,
    status: "Wishlink post pending",
    views: 22400,
    engagementRate: 7.1,
    autoDms: 0,
    clicks: 0,
    ctr: 0,
    orders: 0,
    conversion: 0,
    sales: 0,
    cpv: 0,
    likes: 1800,
    comments: 120,
    shares: 90,
    saves: 220,
    addedOn: null,
    productMessage:
      "Wishlink is still ingesting this post. Sales and attribution will appear here shortly.",
    performanceByBrandId: {
      [PB.AMAZON]: { clicks: 0, orders: 0, sales: 0 },
      [PB.FLIPKART]: { clicks: 0, orders: 0, sales: 0 },
      [PB.MYNTRA]: { clicks: 0, orders: 0, sales: 0 },
      [PB.NYKAA]: { clicks: 0, orders: 0, sales: 0 },
      [PB.TIRA]: { clicks: 0, orders: 0, sales: 0 },
      [PB.WEBSITE]: { clicks: 0, orders: 0, sales: 0 },
    },
  },
  {
    id: "sample-post-6",
    cvxpId: "sample-cvxp-6",
    postId: "sample-post-6",
    postUrl: "https://www.instagram.com/p/sample-fitness/",
    post_thumbnail:
      "https://images.unsplash.com/photo-1571019613454-1cb2f99b2d8b?w=400&h=533&fit=crop",
    creatorUsername: "fitness_karan",
    creatorId: null,
    status: null,
    views: 67200,
    engagementRate: 5.9,
    autoDms: 120,
    clicks: 156,
    ctr: 2.0,
    orders: 14,
    conversion: 4.5,
    sales: 560000,
    cpv: 62,
    likes: 5100,
    comments: 400,
    shares: 890,
    saves: 1200,
    addedOn: null,
    performanceByBrandId: {
      [PB.AMAZON]: { clicks: 52, orders: 5, sales: 210000 },
      [PB.FLIPKART]: { clicks: 38, orders: 4, sales: 145000 },
      [PB.MYNTRA]: { clicks: 28, orders: 3, sales: 110000 },
      [PB.NYKAA]: { clicks: 22, orders: 2, sales: 65000 },
      [PB.TIRA]: { clicks: 12, orders: 0, sales: 20000 },
      [PB.WEBSITE]: { clicks: 4, orders: 0, sales: 10000 },
    },
  },
  {
    id: "sample-post-7",
    cvxpId: "sample-cvxp-7",
    postId: "sample-post-7",
    postUrl: "https://www.instagram.com/reel/sample-amazon-lip/",
    post_thumbnail:
      "https://images.unsplash.com/photo-1586495777744-4413f21062fa?w=400&h=533&fit=crop",
    creatorUsername: "amazon_lipstick_guru",
    creatorId: null,
    status: "Amazon pending",
    views: 145000,
    engagementRate: 8.1,
    autoDms: 520,
    clicks: 192,
    ctr: 2.5,
    orders: 15,
    conversion: 4.0,
    sales: 780000,
    cpv: 78,
    likes: 7800,
    comments: 620,
    shares: 1800,
    saves: 2400,
    addedOn: null,
    isWhitelistingRequested: true,
    performanceByBrandId: {
      [PB.AMAZON]: { clicks: 0, orders: 0, sales: 0 },
      [PB.FLIPKART]: { clicks: 58, orders: 6, sales: 310000 },
      [PB.MYNTRA]: { clicks: 48, orders: 5, sales: 220000 },
      [PB.NYKAA]: { clicks: 42, orders: 3, sales: 180000 },
      [PB.TIRA]: { clicks: 28, orders: 1, sales: 50000 },
      [PB.WEBSITE]: { clicks: 16, orders: 0, sales: 20000 },
    },
  },
  {
    id: "sample-post-8",
    cvxpId: "sample-cvxp-8",
    postId: "sample-post-8",
    postUrl: "https://www.instagram.com/p/sample-affiliate/",
    post_thumbnail:
      "https://images.unsplash.com/photo-1434389677669-e08b4cac3105?w=400&h=533&fit=crop",
    creatorUsername: "affiliate_ravi",
    creatorId: null,
    status: "Non-Wishlink",
    views: 98800,
    engagementRate: 4.6,
    autoDms: 95,
    clicks: 94,
    ctr: 1.6,
    orders: 5,
    conversion: 3.1,
    sales: 95000,
    cpv: 102,
    likes: 3200,
    comments: 210,
    shares: 560,
    saves: 780,
    addedOn: null,
    productMessage:
      "Campaign attribution is incomplete. Ask the creator to tag Wishlink on this post.",
    performanceByBrandId: {
      [PB.AMAZON]: { clicks: 28, orders: 2, sales: 38000 },
      [PB.FLIPKART]: { clicks: 22, orders: 2, sales: 28000 },
      [PB.MYNTRA]: { clicks: 18, orders: 1, sales: 19000 },
      [PB.NYKAA]: { clicks: 14, orders: 0, sales: 10000 },
      [PB.TIRA]: { clicks: 8, orders: 0, sales: 0 },
      [PB.WEBSITE]: { clicks: 4, orders: 0, sales: 0 },
    },
  },
];

/** Platforms shown in the sample content drawer (`TrackingContentDrawer`). */
export const SAMPLE_DRAWER_AVAILABLE_PLATFORMS = [
  { id: 7, name: "Amazon" },
  { id: 303, name: "Flipkart" },
  { id: 227, name: "Myntra" },
  { id: 302, name: "Nykaa" },
  { id: 273, name: "Tira" },
  { id: 999991, name: "Website" },
];

const SAMPLE_PRODUCT_ROWS_DEFAULT = [
  {
    product_name: "Pilgrim lip oil — Dubai Bling",
    sku_id: "sample-sku-1",
    thumbnail: null,
    platform_name: "Amazon",
    orders: 5,
    sales: 380000,
  },
  {
    product_name: "Pilgrim lip oil — Dubai Bling",
    sku_id: "sample-sku-2",
    thumbnail: null,
    platform_name: "Flipkart",
    orders: 4,
    sales: 240000,
  },
  {
    product_name: "Pilgrim lip oil — Dubai Bling",
    sku_id: "sample-sku-3",
    thumbnail: null,
    platform_name: "Myntra",
    orders: 3,
    sales: 150000,
  },
  {
    product_name: "Pilgrim lip oil — Dubai Bling",
    sku_id: "sample-sku-4",
    thumbnail: null,
    platform_name: "Nykaa",
    orders: 2,
    sales: 120000,
  },
  {
    product_name: "Pilgrim lip oil — Dubai Bling",
    sku_id: "sample-sku-5",
    thumbnail: null,
    platform_name: "Tira",
    orders: 1,
    sales: 45000,
  },
];

/** Amazon sales row pending whitelist — matches “Amazon pending” drawer story */
const SAMPLE_PRODUCT_ROWS_AMAZON_PENDING = [
  {
    product_name: "Attribution pending (Amazon)",
    sku_id: "amz-pending-1",
    thumbnail: null,
    platform_name: "Amazon",
    orders: 0,
    sales: 0,
  },
  {
    product_name: "Night serum — 30ml",
    sku_id: "sample-sku-fk-1",
    thumbnail: null,
    platform_name: "Flipkart",
    orders: 3,
    sales: 180000,
  },
  {
    product_name: "Night serum — 30ml",
    sku_id: "sample-sku-my-1",
    thumbnail: null,
    platform_name: "Myntra",
    orders: 2,
    sales: 120000,
  },
  {
    product_name: "Night serum — 30ml",
    sku_id: "sample-sku-nk-1",
    thumbnail: null,
    platform_name: "Nykaa",
    orders: 2,
    sales: 95000,
  },
];

const SAMPLE_PRODUCT_ROWS_NON_WISHLINK = [
  {
    product_name: "Tracked SKU — partial",
    sku_id: "nw-1",
    thumbnail: null,
    platform_name: "Flipkart",
    orders: 2,
    sales: 85000,
  },
  {
    product_name: "Tracked SKU — partial",
    sku_id: "nw-2",
    thumbnail: null,
    platform_name: "Amazon",
    orders: 1,
    sales: 42000,
  },
];

const SAMPLE_PRODUCT_ROWS_SINGLE = [
  {
    product_name: "Promo code product",
    sku_id: "ni-1",
    thumbnail: null,
    platform_name: "Flipkart",
    orders: 1,
    sales: 8500,
  },
];

const SAMPLE_PRODUCT_ROWS_FITNESS = [
  {
    product_name: "Whey protein — 1kg",
    sku_id: "fit-1",
    thumbnail: null,
    platform_name: "Amazon",
    orders: 5,
    sales: 210000,
  },
  {
    product_name: "Whey protein — 1kg",
    sku_id: "fit-2",
    thumbnail: null,
    platform_name: "Flipkart",
    orders: 4,
    sales: 145000,
  },
  {
    product_name: "Shaker bottle",
    sku_id: "fit-3",
    thumbnail: null,
    platform_name: "Myntra",
    orders: 3,
    sales: 110000,
  },
  {
    product_name: "BCAA — 400g",
    sku_id: "fit-4",
    thumbnail: null,
    platform_name: "Nykaa",
    orders: 2,
    sales: 65000,
  },
];

function computeSampleDrawerPerformance(row, drawerPlatforms) {
  const byBrand = row.performanceByBrandId;
  if (!byBrand) {
    return { clicks: row.clicks, orders: row.orders, sales: row.sales };
  }
  if (drawerPlatforms == null || drawerPlatforms.size === 0) {
    return { clicks: row.clicks, orders: row.orders, sales: row.sales };
  }
  let clicks = 0;
  let orders = 0;
  let sales = 0;
  drawerPlatforms.forEach((id) => {
    const block = byBrand[id];
    if (block) {
      clicks += block.clicks;
      orders += block.orders;
      sales += block.sales;
    }
  });
  return { clicks, orders, sales };
}

function pickSampleProductRows(row) {
  if (row.sampleProductRows) return row.sampleProductRows;
  switch (row.status) {
    case "Amazon pending":
      return SAMPLE_PRODUCT_ROWS_AMAZON_PENDING;
    case "Non-Wishlink":
      return SAMPLE_PRODUCT_ROWS_NON_WISHLINK;
    case "Non-Instagram":
      return SAMPLE_PRODUCT_ROWS_SINGLE;
    case "Wishlink post pending":
      return [];
    default:
      break;
  }
  if (row.creatorUsername === "fitness_karan")
    return SAMPLE_PRODUCT_ROWS_FITNESS;
  return SAMPLE_PRODUCT_ROWS_DEFAULT;
}

/**
 * Maps a funnel row to `TrackingContentDrawer` API shape for `/tracking/detail?id=sample`.
 * @param {object} row — one of {@link SAMPLE_FUNNEL_POSTS}
 * @param {Set<number|string>|null} [drawerPlatforms] — selected brand IDs from drawer chips (subset performance).
 */
export function mapSampleFunnelRowToContentDetail(row, drawerPlatforms = null) {
  if (!row) return null;
  const titleHints = {
    makeupbyzainabfaiz: "Bling Lipsticks✨",
    glow_skin_riya: "Glass skin routine ✨",
    stylebyananya: "Festive fit check 🪔",
    shorts_deals_hunter: "YouTube Shorts — promo clip",
    wishlink_creators: "New reel — syncing",
    fitness_karan: "Morning mobility flow",
    amazon_lipstick_guru: "Amazon haul — lipsticks",
    affiliate_ravi: "Affiliate try-on",
  };
  const title =
    titleHints[row.creatorUsername] || `Content by @${row.creatorUsername}`;

  const performance = computeSampleDrawerPerformance(row, drawerPlatforms);
  const isAmazonPending = row.status === "Amazon pending";

  return {
    post_info: {
      post_url: row.postUrl,
      thumbnail_url: row.post_thumbnail,
      title,
      creator_username: row.creatorUsername,
      creator_profile_pic_url: null,
    },
    social_metrics: {
      views: row.views,
      likes: row.likes,
      comments: row.comments,
      shares: row.shares,
      saves: row.saves,
      auto_dms: row.autoDms,
    },
    overview: {
      views: Math.round(row.views * 0.5),
      engagement_rate: row.engagementRate,
      auto_dms: row.autoDms,
      cpv: row.cpv,
    },
    available_platforms: SAMPLE_DRAWER_AVAILABLE_PLATFORMS,
    performance,
    products: pickSampleProductRows(row),
    product_message: row.productMessage ?? null,
    is_amazon_pending: isAmazonPending,
    is_amazon_whitelisting_requested: Boolean(row.isWhitelistingRequested),
  };
}

/** Raw overview payload shape expected by `useCampaignOverview` */
export const SAMPLE_CAMPAIGN_OVERVIEW_RAW = {
  total_posts: 8,
  total_views: 748900,
  engagement_rate: 6.4,
  total_auto_dm: 2512,
  total_clicks: 971,
  ctr: 1.9,
  total_orders: 79,
  total_sales: 5410500,
  cpv: 0.41,
};

const _d = (clicks, orders, sales) => ({ clicks, orders, sales });

/** Distribution donut data for sample campaign (platform split ≈ funnel mix) */
export const SAMPLE_CAMPAIGN_DISTRIBUTION = {
  platform: {
    amazon: _d(210, 18, 980000),
    flipkart: _d(198, 22, 1120000),
    myntra: _d(165, 16, 890000),
    nykaa: _d(142, 12, 720000),
    tira: _d(98, 9, 410000),
    website: _d(52, 4, 185000),
  },
  creator_type: {
    mega: _d(120, 14, 620000),
    macro: _d(185, 20, 980000),
    mid: _d(210, 24, 1120000),
    micro: _d(350, 23, 895000),
  },
};
